import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiBaseUrl, publicMediaSrc } from "../config/api";
import { adminJson } from "../utils/adminSession";
import AdminConfirmDialog from "../components/AdminConfirmDialog";

const EMPTY = {
  categoryName: "",
  slug: "",
  categoryPhoto: "",
  categoryPhotoAlt: "",
  description: "",
  seoTitle: "",
  seoDescription: "",
  isPublished: true,
};
const NAME_MAX = 100;
const SLUG_MAX = 120;
const ALT_MAX = 160;
const DESC_MAX = 160;
const SEO_TITLE_MAX = 60;
const SEO_DESC_MAX = 160;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const PAGE_SIZE = 6;
const PHOTO_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const SLUG_PREFIX = "/category/";
const PREVIEW_HOST = "atindustrialsupply.com";
const inputClass =
  "w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-at-dark outline-none focus:border-at-gold";

function toSlug(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX);
}

function stripCompanySuffix(siteName) {
  return String(siteName || "")
    .replace(/\s*\(\s*m\s*\)\s*(sdn|sbn)\.?\s*bhd\.?\s*$/i, "")
    .replace(/\s+(sdn|sbn)\.?\s*bhd\.?\s*$/i, "")
    .trim();
}

function imageAltFromName(name) {
  const n = String(name || "").trim();
  return n ? `Industrial ${n}`.slice(0, ALT_MAX) : "";
}

function descriptionFromAlt(alt) {
  const a = String(alt || "").trim();
  return a ? `Browse ${a}`.slice(0, DESC_MAX) : "";
}

function seoDescriptionFromAlt(alt, siteName) {
  const a = String(alt || "").trim();
  if (!a) return "";
  const site = String(siteName || "").trim();
  const text = site ? `Source ${a} from ${site}` : `Source ${a}`;
  return text.slice(0, SEO_DESC_MAX);
}

function buildSeoTitle(categoryName, siteName) {
  const namePart = String(categoryName || "")
    .replace(/\bproducts\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
  const industrial = `Industrial ${namePart} Supplier Malaysia`.replace(/\s+/g, " ").trim();
  const fullSite = String(siteName || "").trim();
  let title = fullSite ? `${industrial} | ${fullSite}` : industrial;
  if (title.length <= SEO_TITLE_MAX) return title;
  const shortSite = stripCompanySuffix(fullSite);
  if (shortSite && shortSite !== fullSite) {
    title = `${industrial} | ${shortSite}`;
    if (title.length <= SEO_TITLE_MAX) return title;
  }
  if (industrial.length <= SEO_TITLE_MAX) return industrial;
  return industrial.slice(0, SEO_TITLE_MAX).trim();
}

const UNTOUCHED = { slug: false, alt: false, desc: false, seoTitle: false, seoDesc: false };

function isPublished(item) {
  return item?.isPublished !== false;
}

function AdminCategoryPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const editedRef = useRef(UNTOUCHED);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [siteName, setSiteName] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return categories.filter((item) => {
      const nameMatch = !q || String(item.categoryName || "").toLowerCase().includes(q) || String(item.slug || "").includes(q);
      const published = isPublished(item);
      const statusMatch =
        statusFilter === "all" || (statusFilter === "published" && published) || (statusFilter === "draft" && !published);
      return nameMatch && statusMatch;
    });
  }, [categories, search, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [list, contacts] = await Promise.all([
          fetch(`${getApiBaseUrl()}/categories`, { headers: { Accept: "application/json" } }).then((res) => {
            if (!res.ok) throw new Error("Unable to load categories");
            return res.json();
          }),
          fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } }).then((res) => (res.ok ? res.json() : [])),
        ]);
        if (!cancelled) {
          setCategories(Array.isArray(list) ? list : []);
          setSiteName(Array.isArray(contacts) && contacts[0]?.siteName ? contacts[0].siteName : "");
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || "Unable to load categories");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  function resetEdited() {
    editedRef.current = { ...UNTOUCHED };
  }

  function markEdited(patch) {
    editedRef.current = { ...editedRef.current, ...patch };
  }

  useEffect(() => {
    if (!panelOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [panelOpen]);

  useEffect(() => {
    if (!panelOpen || editingId) return;
    const flags = editedRef.current;
    setForm((prev) => {
      if (!prev.categoryName.trim()) return prev;
      const alt = flags.alt ? prev.categoryPhotoAlt : imageAltFromName(prev.categoryName);
      return {
        ...prev,
        categoryPhotoAlt: flags.alt ? prev.categoryPhotoAlt : alt,
        description: flags.desc ? prev.description : descriptionFromAlt(alt),
        seoTitle: flags.seoTitle ? prev.seoTitle : buildSeoTitle(prev.categoryName, siteName),
        seoDescription: flags.seoDesc ? prev.seoDescription : seoDescriptionFromAlt(alt, siteName),
      };
    });
  }, [siteName, panelOpen, editingId]);

  function onUnauthorized(err) {
    if (err?.status === 401) {
      navigate("/login", { replace: true });
      return true;
    }
    return false;
  }

  function openCreate() {
    setEditingId(null);
    setForm(EMPTY);
    resetEdited();
    setError("");
    setNotice("");
    setPanelOpen(true);
  }

  function openEdit(item) {
    const name = item.categoryName || "";
    const alt = item.categoryPhotoAlt || imageAltFromName(name);
    setEditingId(item._id);
    setForm({
      categoryName: name,
      slug: item.slug || toSlug(name),
      categoryPhoto: item.categoryPhoto || "",
      categoryPhotoAlt: alt,
      description: item.description || descriptionFromAlt(alt),
      seoTitle: item.seoTitle || buildSeoTitle(name, siteName),
      seoDescription: item.seoDescription || seoDescriptionFromAlt(alt, siteName),
      isPublished: isPublished(item),
    });
    resetEdited();
    setError("");
    setNotice("");
    setPanelOpen(true);
  }

  function closePanel() {
    setPanelOpen(false);
    setEditingId(null);
    setForm(EMPTY);
    resetEdited();
  }

  function onNameChange(value) {
    const flags = editedRef.current;
    setForm((prev) => {
      const next = { ...prev, categoryName: value };
      if (!flags.slug) next.slug = toSlug(value);
      if (!flags.alt) next.categoryPhotoAlt = imageAltFromName(value);
      const alt = !flags.alt ? next.categoryPhotoAlt : prev.categoryPhotoAlt;
      if (!flags.desc) next.description = descriptionFromAlt(alt);
      if (!flags.seoTitle) next.seoTitle = buildSeoTitle(value, siteName);
      if (!flags.seoDesc) next.seoDescription = seoDescriptionFromAlt(alt, siteName);
      return next;
    });
  }

  function onAltChange(value) {
    const flags = editedRef.current;
    markEdited({ alt: true });
    setForm((prev) => {
      const next = { ...prev, categoryPhotoAlt: value };
      if (!flags.desc) next.description = descriptionFromAlt(value);
      if (!flags.seoDesc) next.seoDescription = seoDescriptionFromAlt(value, siteName);
      return next;
    });
  }

  async function onPickFile(file) {
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      setError("Category photo must be a JPEG, PNG or WebP file");
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      setError("Category photo must be 5 MB or smaller");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(new Error("Unable to read file"));
        reader.readAsDataURL(file);
      });
      const uploaded = await adminJson("/media", {
        method: "POST",
        body: {
          purpose: "category",
          filename: file.name,
          contentType: file.type,
          data,
        },
      });
      setForm((prev) => ({
        ...prev,
        categoryPhoto: uploaded.path,
      }));
    } catch (err) {
      if (onUnauthorized(err)) return;
      setError(err?.message || "Unable to upload photo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function onSave(publish) {
    const name = form.categoryName.trim();
    const slug = form.slug.trim();
    const alt = form.categoryPhotoAlt.trim();
    if (!name) {
      setError("Category name is required");
      return;
    }
    if (name.length > NAME_MAX) {
      setError(`Category name must be ${NAME_MAX} characters or fewer`);
      return;
    }
    if (!slug || !SLUG_RE.test(slug)) {
      setError("Slug can only contain lowercase letters, numbers and hyphens");
      return;
    }
    if (!form.categoryPhoto.trim()) {
      setError("Category photo is required");
      return;
    }
    if (!alt) {
      setError("Image alt text is required");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = {
        categoryName: name,
        slug,
        categoryPhoto: form.categoryPhoto.trim(),
        categoryPhotoAlt: alt,
        description: form.description.trim(),
        seoTitle: form.seoTitle.trim(),
        seoDescription: form.seoDescription.trim(),
        isPublished: Boolean(publish),
      };
      if (editingId) {
        const saved = await adminJson(`/categories/${editingId}`, { method: "PUT", body: payload });
        setCategories((prev) =>
          prev.map((item) => (item._id === saved._id ? { ...saved, productCount: item.productCount || 0 } : item))
        );
        setNotice("Category updated.");
      } else {
        const created = await adminJson("/categories", { method: "POST", body: payload });
        setCategories((prev) => [{ ...created, productCount: 0 }, ...prev]);
        setNotice("Category created.");
      }
      closePanel();
    } catch (err) {
      if (onUnauthorized(err)) return;
      setError(err?.message || "Unable to save category");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(item) {
    setDeleteTarget(item);
  }

  async function confirmDelete() {
    const item = deleteTarget;
    if (!item) return;
    setError("");
    setNotice("");
    setSaving(true);
    try {
      await adminJson(`/categories/${item._id}`, { method: "DELETE" });
      setCategories((prev) => prev.filter((row) => row._id !== item._id));
      setNotice("Category deleted.");
      setDeleteTarget(null);
      if (editingId === item._id) closePanel();
    } catch (err) {
      if (onUnauthorized(err)) return;
      setError(err?.message || "Unable to delete category");
    } finally {
      setSaving(false);
    }
  }

  const photoSrc = publicMediaSrc(form.categoryPhoto);
  const from = filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const to = Math.min(filtered.length, currentPage * PAGE_SIZE);
  const previewTitle = form.seoTitle.trim() || form.categoryName.trim() || "Category title";
  const previewUrl = `${PREVIEW_HOST}${SLUG_PREFIX}${form.slug.trim() || "slug"}`;
  const previewDesc = form.seoDescription.trim() || form.description.trim() || "Write a short description for Google search results.";

  return (
    <div className="-mt-2">
      <p className="text-xs text-neutral-400">Dashboard / Categories</p>
      <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-at-dark">Product Categories</h1>
          <p className="mt-1 text-sm text-neutral-500">Create and manage product categories and their search appearance.</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="rounded-md bg-at-gold px-4 py-2 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
        >
          + Add Category
        </button>
      </div>

      {error && !panelOpen && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {notice && <p className="mt-4 text-sm text-emerald-700">{notice}</p>}

      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <p className="text-sm font-medium text-at-dark">
          {loading ? "Loading…" : `${filtered.length} ${filtered.length === 1 ? "Category" : "Categories"}`}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search categories..."
            className={`${inputClass} max-w-xs`}
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`${inputClass} w-auto`}
            aria-label="Filter by status"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="mt-8 text-sm text-neutral-500">Loading categories…</p>
      ) : filtered.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-neutral-300 bg-white px-6 py-16 text-center">
          <p className="text-base font-semibold text-at-dark">No categories yet</p>
          <p className="mt-1 text-sm text-neutral-500">Add a category to organise products on the website.</p>
          <button
            type="button"
            onClick={openCreate}
            className="mt-5 rounded-md bg-at-gold px-4 py-2 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
          >
            + Add Category
          </button>
        </div>
      ) : (
        <div className="mt-5 overflow-hidden rounded-xl border border-neutral-200 bg-white">
          {paged.map((item) => {
            const published = isPublished(item);
            const count = Number(item.productCount) || 0;
            return (
              <div key={item._id} className="flex items-center gap-3 border-b border-neutral-100 px-4 py-3 last:border-b-0">
                <span
                  className={`h-2.5 w-2.5 shrink-0 rounded-full ${published ? "bg-emerald-500" : "bg-neutral-400"}`}
                  title={published ? "Published" : "Draft"}
                  aria-label={published ? "Published" : "Draft"}
                />
                <div className="h-12 w-16 shrink-0 overflow-hidden rounded bg-neutral-100">
                  {publicMediaSrc(item.categoryPhoto) ? (
                    <img src={publicMediaSrc(item.categoryPhoto)} alt={item.categoryPhotoAlt || ""} className="h-full w-full object-cover" />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-at-dark">
                    {item.categoryName}({count})
                  </p>
                  {item.slug ? <p className="text-xs text-neutral-400">/{item.slug}</p> : null}
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(item)}
                  className="rounded-md p-2 text-neutral-500 hover:bg-neutral-100 hover:text-at-dark"
                  aria-label={`Edit ${item.categoryName}`}
                >
                  <PencilIcon />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(item)}
                  className="rounded-md p-2 text-neutral-500 hover:bg-red-50 hover:text-red-600"
                  aria-label={`Delete ${item.categoryName}`}
                >
                  <TrashIcon />
                </button>
              </div>
            );
          })}
        </div>
      )}
      {filtered.length > 0 && (
        <div className="mt-6 flex flex-col items-center justify-between gap-3 text-sm text-neutral-500 sm:flex-row">
          <p>
            Showing {from}–{to} of {filtered.length} categories
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-md border border-neutral-200 bg-white px-3 py-1.5 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="rounded-md bg-at-gold px-3 py-1.5 text-at-dark">{currentPage}</span>
            <button
              type="button"
              disabled={currentPage >= pageCount}
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              className="rounded-md border border-neutral-200 bg-white px-3 py-1.5 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {panelOpen && (
        <div className="fixed inset-0 z-50 flex h-[100dvh] max-h-[100dvh] justify-end overflow-hidden bg-black/30">
          <button type="button" aria-label="Close panel" className="hidden h-full flex-1 sm:block" onClick={closePanel} />
          <aside className="flex h-full min-h-0 w-full max-w-md flex-col overflow-hidden bg-white shadow-xl">
            <div className="flex shrink-0 items-start justify-between border-b border-neutral-100 px-6 py-5">
              <div>
                <h2 className="text-xl font-semibold text-at-dark">{editingId ? "Edit Category" : "Add Category"}</h2>
                <p className="mt-1 text-sm text-neutral-500">
                  {editingId ? "Update this product category." : "Create a new product category."}
                </p>
              </div>
              <button type="button" onClick={closePanel} className="text-2xl leading-none text-neutral-400 hover:text-at-dark" aria-label="Close">
                ×
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5">
              {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

              <h3 className="text-sm font-semibold text-at-dark">Category Information</h3>

              <label className="mt-4 block text-sm font-medium text-at-dark" htmlFor="categoryName">
                Category Name *
              </label>
              <input
                id="categoryName"
                value={form.categoryName}
                maxLength={NAME_MAX}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="e.g. Steel Products"
                className={`${inputClass} mt-1.5`}
              />

              <label className="mt-4 block text-sm font-medium text-at-dark" htmlFor="slug">
                URL Slug *
              </label>
              <div className="mt-1.5 flex overflow-hidden rounded-md border border-neutral-200 focus-within:border-at-gold">
                <span className="shrink-0 bg-neutral-100 px-2 py-2 text-xs text-neutral-500">{SLUG_PREFIX}</span>
                <input
                  id="slug"
                  value={form.slug}
                  maxLength={SLUG_MAX}
                  onChange={(e) => {
                    markEdited({ slug: true });
                    setForm((prev) => ({ ...prev, slug: e.target.value.toLowerCase() }));
                  }}
                  placeholder="steel-products"
                  className="w-full bg-white px-2 py-2 text-sm text-at-dark outline-none"
                />
              </div>
              <p className="mt-1 text-xs text-neutral-400">Use lowercase letters, numbers and hyphens.</p>

              <p className="mt-4 text-sm font-medium text-at-dark">Category Photo *</p>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  onPickFile(e.dataTransfer.files?.[0]);
                }}
                className={`mt-1.5 rounded-lg border-2 border-dashed px-4 py-8 text-center ${
                  dragOver ? "border-at-gold bg-amber-50" : "border-neutral-200 bg-neutral-50"
                }`}
              >
                {photoSrc ? (
                  <img src={photoSrc} alt={form.categoryPhotoAlt || ""} className="mx-auto mb-3 h-20 w-full object-contain" />
                ) : (
                  <p className="mb-2 text-2xl text-neutral-300">▢</p>
                )}
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                  className="text-sm font-medium text-at-dark underline disabled:opacity-60"
                >
                  {uploading ? "Uploading…" : "Upload category image"}
                </button>
                <p className="mt-2 text-xs text-neutral-400">JPG, PNG or WebP · Recommended 1200 × 800 px · Max 5 MB</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => onPickFile(e.target.files?.[0])}
                />
              </div>

              <label className="mt-4 block text-sm font-medium text-at-dark" htmlFor="categoryPhotoAlt">
                Image Alt Text *
              </label>
              <input
                id="categoryPhotoAlt"
                value={form.categoryPhotoAlt}
                maxLength={ALT_MAX}
                onChange={(e) => onAltChange(e.target.value)}
                placeholder="Describe the image for accessibility"
                className={`${inputClass} mt-1.5`}
              />
              <p className="mt-1 text-right text-xs text-neutral-400">
                {form.categoryPhotoAlt.length} / {ALT_MAX}
              </p>

              <label className="mt-4 block text-sm font-medium text-at-dark" htmlFor="description">
                Description
              </label>
              <textarea
                id="description"
                rows={3}
                value={form.description}
                maxLength={DESC_MAX}
                onChange={(e) => {
                  markEdited({ desc: true });
                  setForm((prev) => ({ ...prev, description: e.target.value }));
                }}
                placeholder="Describe the products included in this category"
                className={`${inputClass} mt-1.5 resize-y`}
              />
              <p className="mt-1 text-right text-xs text-neutral-400">
                {form.description.length} / {DESC_MAX}
              </p>

              <h3 className="mt-8 text-sm font-semibold text-at-dark">SEO Settings</h3>

              <label className="mt-4 block text-sm font-medium text-at-dark" htmlFor="seoTitle">
                SEO Title
              </label>
              <input
                id="seoTitle"
                value={form.seoTitle}
                maxLength={SEO_TITLE_MAX}
                onChange={(e) => {
                  markEdited({ seoTitle: true });
                  setForm((prev) => ({ ...prev, seoTitle: e.target.value }));
                }}
                placeholder={siteName ? `Steel Products Supplier Malaysia | ${siteName}` : "Steel Products Supplier Malaysia"}
                className={`${inputClass} mt-1.5`}
              />
              <p className="mt-1 text-right text-xs text-neutral-400">
                {form.seoTitle.length} / {SEO_TITLE_MAX}
              </p>

              <label className="mt-4 block text-sm font-medium text-at-dark" htmlFor="seoDescription">
                SEO Description
              </label>
              <textarea
                id="seoDescription"
                rows={3}
                value={form.seoDescription}
                maxLength={SEO_DESC_MAX}
                onChange={(e) => {
                  markEdited({ seoDesc: true });
                  setForm((prev) => ({ ...prev, seoDescription: e.target.value }));
                }}
                placeholder="Write a short description for Google search results"
                className={`${inputClass} mt-1.5 resize-y`}
              />
              <p className="mt-1 text-right text-xs text-neutral-400">
                {form.seoDescription.length} / {SEO_DESC_MAX}
              </p>

              <p className="mt-4 text-sm font-medium text-at-dark">Google Search Preview</p>
              <div className="mt-1.5 rounded-md border border-neutral-200 bg-white px-3 py-3">
                <p className="text-sm text-[#1a0dab]">{previewTitle}</p>
                <p className="truncate text-xs text-emerald-700">{previewUrl}</p>
                <p className="mt-1 text-xs leading-snug text-neutral-500">{previewDesc}</p>
              </div>
            </div>

            <div className="shrink-0 border-t border-neutral-100 px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium text-at-dark">Publishing</span>
                <label className="flex items-center gap-2 text-sm text-neutral-600">
                  <span
                    className={`relative h-5 w-9 rounded-full ${form.isPublished ? "bg-at-gold" : "bg-neutral-300"}`}
                  >
                    <input
                      type="checkbox"
                      className="peer absolute inset-0 cursor-pointer opacity-0"
                      checked={Boolean(form.isPublished)}
                      onChange={(e) => setForm((prev) => ({ ...prev, isPublished: e.target.checked }))}
                    />
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition ${
                        form.isPublished ? "left-4" : "left-0.5"
                      }`}
                    />
                  </span>
                  Publish category
                </label>
              </div>
              <p className="mb-3 text-xs text-neutral-400">Published categories are visible on the website and included in the sitemap.</p>
              <div className="grid grid-cols-2 gap-2 sm:flex">
                <button
                  type="button"
                  onClick={closePanel}
                  className="rounded-md border border-neutral-300 px-3 py-2.5 text-sm font-medium text-at-dark"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={saving || uploading}
                  onClick={() => onSave(false)}
                  className="rounded-md border border-neutral-300 px-3 py-2.5 text-sm font-medium text-at-dark disabled:opacity-60 sm:flex-1"
                >
                  Save as Draft
                </button>
                <button
                  type="button"
                  disabled={saving || uploading}
                  onClick={() => onSave(true)}
                  className="col-span-2 rounded-md bg-at-gold px-3 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover disabled:opacity-60 sm:col-auto sm:flex-1"
                >
                  {saving ? "Saving…" : editingId ? "Save Changes" : "Create Category"}
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
      {deleteTarget ? (
        <AdminConfirmDialog
          title="Delete category?"
          message={`Delete “${deleteTarget.categoryName}”? You can restore it from Recent Activities for 30 days.`}
          busy={saving}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      ) : null}
    </div>
  );
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 7h16" />
      <path d="M9 7V5h6v2" />
      <path d="M7 7l1 13h8l1-13" />
    </svg>
  );
}

export default AdminCategoryPage;
